/**
 * The damage math the attack spine records: the rulebook's "CALCULATING DAMAGE" order
 * (armor, then resistance), the type routing that decides which track an instance lands
 * on, and the per-instance dice draws.
 */

import type {
  BurnSetStep,
  Combatant,
  DamageType,
  ResolvedStep,
  WeaponDamage,
  WeaponProfile,
} from "@lancer-vtt/domain";
import { BIOLOGICAL_STATUS, hasStatus, hasStructureTrack } from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import { rollDamage, rollOverkill, type DiceSource, type RollResult } from "../../dice";
import { absorbIntoOvershield } from "../overshield";
import { resolveDamageCascade, resolveHeatCascade } from "../tables";
import type { SelfCheckContext } from "../selfCheck";
import type { RecordedAnswers } from "../effects/choices";
import { foreignHeatDestroy } from "../grunt";

/** Damage types that **ignore armor** (rulebook "ARMOR AND RESISTANCE" / "HEAT"). */
function ignoresArmor(dtype: DamageType): boolean {
  return dtype === "burn" || dtype === "heat";
}

/**
 * **The Armor a hit's weapon damage left unused against `targetId`** (BC-019). RAW Core p.67
 * subtracts the target's ARMOR once "from the total" of an attack's damage, and bonus damage is
 * "added onto" the attack (p.78), so an on-hit `extra_damage` pays only what the weapon's own
 * damage did not use up. An AP instance means the whole attack ignores Armor (0 left); burn and
 * heat use none of it. A hit with no weapon damage (a Ram) leaves all of `armor`.
 */
export function armorLeftAfterHit(steps: readonly ResolvedStep[], targetId: string, armor: number): number {
  let left = armor;
  for (const s of steps) {
    if (s.op !== "damage" || s.target !== targetId || s.source?.kind !== "weapon") continue;
    if (s.ap) return 0;
    if (!ignoresArmor(s.dtype)) left -= s.preMitigation;
  }
  return Math.max(0, left);
}

/**
 * Keep the `keep` highest values of `values`, **preserving the original order** of
 * the kept entries (ties broken by index). Used to apply the crit "keep the better
 * half" over overkill-settled faces. `keep >= length` keeps everything.
 */
export function keepHighest(values: readonly number[], keep: number): number[] {
  if (keep >= values.length) return [...values];
  const keptIdx = values
    .map((v, idx) => ({ idx, v }))
    .sort((a, b) => b.v - a.v || a.idx - b.idx)
    .slice(0, keep)
    .map((x) => x.idx)
    .sort((a, b) => a - b);
  return keptIdx.map((i) => values[i]!);
}

/**
 * Extract the **kept** base d20 face (and, with advantage, the **dropped** one) from a
 * {@link rollD20} result. Advantage (an attack-roll replacement) draws TWO base d20s (`draws[0]`/
 * `draws[1]`) and flags exactly one `kept` (the higher) — read the kept draw *positionally*,
 * not `draws[0]`, so the result reflects the die actually chosen; without advantage this is
 * always `draws[0]`. Shared by the original to-hit roll and Valiant Aid's bound reroll
 * (`third-party-reactions` Phase 2, D6) so both draws are read out identically — the reroll
 * is "redraw the faces, keep the count," and this is the "keep the count" half of that.
 */
export function extractBaseD20(result: RollResult, advantage: boolean): { rawD20: number; advantageDrop: number | undefined } {
  const baseDrawCount = advantage ? 2 : 1;
  const baseKeptIndex = result.kept.findIndex((k, i) => k && i < baseDrawCount);
  const rawD20 = result.draws[baseKeptIndex >= 0 ? baseKeptIndex : 0]!.value;
  const advantageDrop = advantage ? result.draws[baseKeptIndex === 0 ? 1 : 0]!.value : undefined;
  return { rawD20, advantageDrop };
}

/**
 * The recorded outcome of mitigating one damage total against a target. Implements
 * the rulebook "CALCULATING DAMAGE" order:
 *   1. (caller) roll damage + increases.
 *   2. subtract **armor** — skipped if AP, or the damage type ignores armor
 *      (burn/heat). A single attack's armor applies **once**.
 *   3. **resistance** halves (rounded **up**, golden rule II), per damage type.
 *   4. floor at 0.
 *
 * `resistant` is the caller's fold: `foldedResistance` on the attack spine
 * (`damageApply.ts`), `nonAttackResistant` at the non-attack sites. `targetArmor` is likewise
 * already Shredded-gated by the caller: `effectiveTargetArmor` on the attack spine,
 * `nonAttackArmor` at the non-attack sites. The returned fields feed the recorded `DamageStep`.
 *
 * **Exported** so the damage packet (`resolve/damagePacket.ts`), which every non-attack site
 * raises, and GM damage apply the SAME armor math a normal hit gets, rather than re-deriving it.
 */
export type MitigateArgs = {
  preMitigation: number;
  dtype: DamageType;
  ap: boolean;
  targetArmor: number;
  resistant: boolean;
};

export function mitigate(args: MitigateArgs): { armor: number; resisted: boolean; final: number } {
  const { preMitigation, dtype, ap, targetArmor, resistant } = args;
  const armorApplies = !ap && !ignoresArmor(dtype);
  const armor = armorApplies ? Math.max(0, targetArmor) : 0;
  let dmg = Math.max(0, preMitigation - armor);
  const resisted = resistant && dmg > 0;
  if (resisted) dmg = Math.ceil(dmg / 2); // golden rule II: always round up.
  return { armor, resisted, final: Math.max(0, dmg) };
}

/**
 * **The shared ×0.5 damage-scaling op** (`decisions/0108` §1 — `RequestAttack.damageScale`,
 * Covering Fire's RAW "half damage, Heat, or Burn"; `t_heavy_gunner:1`). Halves one damage
 * instance's amount — **independently per instance** — rounded **up** (Golden Rule II, rulebook
 * p.11), applied AFTER crit / Reliable / Overkill / Exposed have settled it and BEFORE Armor: it is
 * the attacker's own reduction, CALCULATING DAMAGE step 1 (Core p.67, `event-engine-damage` R4).
 * Resistance halves it again after Armor ({@link mitigate}). Called once per damage instance (primary + every rider, hit
 * or Reliable-floor, target share or protector share) — a weapon dealing a physical primary plus
 * a Heat or Burn rider halves EACH instance on its own, which is exactly how "damage, Heat, OR
 * Burn" is satisfied without a separate code path per damage type: `dtype` alone decides whether
 * a given instance's halved `final` lands on HP, the Heat track, or a Burn mark, downstream in
 * {@link routeFinalDamage}. `scale` is **server-stamped only** (never client-computed) — see
 * `RequestAttack.damageScale`'s own doc for the two-command-tier rationale. `undefined` ⇒ the
 * amount is returned unchanged (the v1 behavior, every non-scaled attack).
 */
export function applyDamageScale(amount: number, scale: RequestAttack["damageScale"]): number {
  return scale === "half" ? Math.ceil(amount / 2) : amount;
}

/**
 * The target's Armor as the attack's mitigation sees it: `0` when a target-side modifier ignores
 * it (Shredded), else the frozen rating. AP is applied later, per instance, by {@link mitigate}.
 */
export function effectiveTargetArmor(target: Combatant, ignoreTargetArmor: boolean): number {
  return ignoreTargetArmor ? 0 : target.base.armor;
}

/**
 * The three **physical** damage types Exposed (A3) doubles. Burn/heat (and any exotic
 * type) are NOT doubled by Exposed — the status reads "double �, � and �" (kinetic,
 * energy, explosive). A `variable` type resolves to a concrete one before this is read.
 * Exported (`unit-ai`, `ev.ts`'s overlay-fold widening) so a caller outside the resolver
 * folding Exposed's doubling composes this SAME gate rather than re-deriving which types
 * count as "physical."
 */
export function isPhysical(dtype: DamageType): boolean {
  return dtype === "kinetic" || dtype === "energy" || dtype === "explosive";
}

/**
 * Route one damage instance's already-mitigated `final` amount to its target track,
 * by damage type (`npc-weapon-rider-damage` Phase 2, decision 6 — the type routing
 * switch, and the heat-mis-route fix: a `heat`-typed instance previously had NO target
 * branch at all and fell through {@link resolveDamageCascade} straight into HP —
 * Hornet Hex Missiles dealt HP damage on main instead of heat).
 *
 *   - **kinetic/energy/explosive** → the Overshield-then-HP pipeline
 *     ({@link absorbIntoOvershield} then {@link resolveDamageCascade}); no burn mark.
 *   - **burn** → the SAME Overshield-then-HP pipeline (burn IS damage — RAW p.105's
 *     shield-first rule applies) PLUS a `burn_set` mark (rulebook "BURN": take the
 *     damage now, ignoring armor, then mark it for the turn-start tick).
 *   - **heat** → {@link resolveHeatCascade} directly. Heat is NOT damage (RAW "HEAT"):
 *     it never touches the Overshield (which only stops *damage*, p.105) and never
 *     cascades through {@link resolveDamageCascade} — it has its OWN cascade into the
 *     Overheating/Stress table. No burn mark either (heat isn't burn). `final` here is
 *     the caller's {@link mitigate} output for dtype `"heat"`, which already forces
 *     Armor OFF ({@link ignoresArmor}) and folds Resistance in — the RAW heat-track
 *     rule ("Resistance applies, Armor doesn't"). A target with NO heat track at all
 *     (Biological) has `resolveHeatCascade` convert it to an energy damage packet
 *     (`heatAsEnergyDamage`, RAW p.67): the target's Armor, Immunity and Overshield
 *     apply to it, and Exposed doubles it, but Resistance is not applied a second time
 *     (`resisted`), since the caller's `mitigate` already halved it as heat.
 *
 * Exposed's physical-only doubling ({@link isPhysical}) is already excluded from
 * `final` upstream (computed pre-mitigation, before this routes) — heat is never
 * doubled here either way.
 */
export function routeFinalDamage(args: {
  target: Combatant;
  dtype: DamageType;
  final: number;
  dice: DiceSource;
  /** Threaded to a structure-damage cascade's System Trauma row (`system-trauma-destruction` Phase 4). */
  choices?: RecordedAnswers;
  /** Threaded to a structure-damage cascade's System Trauma row — see {@link choices}. */
  interactive?: boolean;
  /** Threaded to a structure/overheating cascade's `turn`-kind status stamp — `expiry-matcher-collapse`, `decisions/0132`. */
  turnOrdinal: number;
  /** Threaded to the structure/overheating table's self-check (the HULL / ENGINEERING roll) — see `tables.ts`. */
  selfCheck?: SelfCheckContext;
  /** Who dealt it: the attacker's id, or `"gm"`. Heat from anyone but the target destroys a Weak Grunt (`grunt.ts`). */
  from: string;
  /** `false`: burn lands without marking (the burn tick deals the marked burn, which stays as it is). */
  mark?: boolean;
}): { steps: ResolvedStep[]; warnings: string[]; destroyed: boolean } {
  const { target, dtype, final, dice, choices, interactive, turnOrdinal, selfCheck, from, mark = true } = args;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  if (dtype === "heat") {
    // `choices`/`interactive` ride the heat branch too, and this is the branch that was DROPPING
    // them (`silent-equipment-autopick` Phase 3 step 10). The Overheating/Stress table owes no
    // pick of its own — the book's only *"of your choice"* destruction row is the Structure
    // Damage table's System Trauma row (`lancer-core-book.txt:4371`, the sole occurrence in
    // 22,843 lines) — but a target with NO heat track (Biological) re-enters the DAMAGE cascade
    // through `heatAsEnergyDamage`, and that cascade can land that row. So the pair is a conduit
    // here, not a feature: an interactive caller stays interactive across the heat→damage
    // re-entry instead of silently auto-picking which of the target's systems is torn off.
    // A target with no heat track takes it as energy damage, sourced to `from`. The caller already
    // mitigated it as heat, Resistance included, so the energy packet doesn't apply Resistance again.
    const heatCascade = resolveHeatCascade({ target, amount: final, dice, choices, interactive, turnOrdinal, selfCheck, source: from === "gm" ? null : from, resisted: true });
    steps.push(...heatCascade.steps);
    warnings.push(...heatCascade.warnings);
    const destroy = foreignHeatDestroy({ target, amount: final, from, steps });
    if (destroy) {
      steps.push(destroy);
      warnings.push(`${target.name}: a Weak Grunt takes heat from another source and is destroyed.`);
    }
    return { steps, warnings, destroyed: heatCascade.destroyed || destroy !== undefined };
  }

  // The Overshield pipeline stop (`weapon-tag-mechanics` Phase 4, decision 12; RAW
  // Core p.105 "Damage is dealt to OVERSHIELD first, then HP") — drains the shield `target`
  // has left (the caller folds what came before); only the remainder cascades to HP.
  const shieldAbsorb = absorbIntoOvershield({ target, amount: final });
  steps.push(...shieldAbsorb.steps);
  if (shieldAbsorb.absorbed > 0) {
    warnings.push(`${target.name}: OVERSHIELD absorbs ${shieldAbsorb.absorbed} of the ${final} damage.`);
  }
  const cascade = resolveDamageCascade({ target, amount: shieldAbsorb.remaining, dice, choices, interactive, turnOrdinal, selfCheck });
  steps.push(...cascade.steps);
  warnings.push(...cascade.warnings);

  if (dtype === "burn" && final > 0 && mark) {
    const prev = target.current.burn ?? 0;
    const burnStep: BurnSetStep = { op: "burn_set", target: target.id, burn: prev + final, prev };
    steps.push(burnStep);
  }
  return { steps, warnings, destroyed: cascade.destroyed };
}

/**
 * Roll **one damage instance's** dice (`npc-weapon-rider-damage` Phase 3, decisions 3+5 —
 * sequential per-instance resolution; crit doubles rider dice too, reusing the exact same
 * overkill/keep-highest machinery the primary instance already used, since `weapon.overkill` is a
 * weapon-wide tag every one of its instances shares). A flat `count: 0` entry — every shipping
 * rider, per the data survey — rolls no dice at all: the bonus IS the damage. `inject` (the area
 * fan-out's once-rolled shared pool per instance, decision 8) short-circuits the draw entirely,
 * picking the base or crit value by `isCrit` — never re-rolled per target, exactly like the
 * Phase-1 primary-only path.
 */
export function rollInstanceDice(args: {
  entry: WeaponDamage;
  isCrit: boolean;
  overkill: boolean;
  dice: DiceSource;
  inject?: { baseDice: number[]; critDice: number[]; bonus: number };
  /**
   * A `maximize` roll replacement (t_brutal:1) — when `true`, every
   * kept die shows its MAX face instead of drawing (no roll, `overkillHeat: 0` — a maxed
   * die never shows a `1`, so no reroll ever triggers). `entry.count` kept dice regardless
   * of crit/overkill: crit's own count*2-draw-keep-highest and overkill's reroll-on-1 both
   * exist to find the best `count` dice out of a noisy draw — maximizing skips the noise
   * and goes straight to the best possible outcome those mechanisms could ever produce.
   */
  maximize?: boolean;
}): { dice: number[]; damageBonus: number; overkillHeat: number } {
  const { entry, isCrit, overkill, dice, inject, maximize } = args;
  if (inject) {
    return { dice: isCrit ? inject.critDice : inject.baseDice, damageBonus: inject.bonus, overkillHeat: 0 };
  }
  if (entry.count === 0) {
    return { dice: [], damageBonus: entry.bonus, overkillHeat: 0 };
  }
  if (maximize) {
    return { dice: new Array(entry.count).fill(entry.sides) as number[], damageBonus: entry.bonus, overkillHeat: 0 };
  }
  if (overkill) {
    const pool = isCrit ? entry.count * 2 : entry.count;
    const ok = rollOverkill(dice, pool, entry.sides);
    const settled = ok.dice.map((d) => d.final);
    return { dice: isCrit ? keepHighest(settled, entry.count) : settled, damageBonus: entry.bonus, overkillHeat: ok.heat };
  }
  const dmgResult = rollDamage({ source: dice, count: entry.count, sides: entry.sides, crit: isCrit });
  return { dice: dmgResult.draws.filter((_, i) => dmgResult.kept[i]).map((d) => d.value), damageBonus: entry.bonus, overkillHeat: 0 };
}

/**
 * **Archaic** (`tg_archaic`, Core p.105: "This weapon is old-fashioned and can't harm mechs"): does
 * this weapon deal no damage to `target`? A mech is read as a character with a Structure track
 * that isn't Biological: mechs and NPC mechs carry one, a pilot on foot, a drone and an object
 * don't, and a Biological NPC (a monstrosity) is no mech. The attack still rolls to hit and its
 * on-hit effects still apply; only its weapon damage, Reliable floor included, is skipped.
 */
export function archaicCantHarm(weapon: WeaponProfile, target: Combatant): boolean {
  return weapon.tags?.some((t) => t.id === "tg_archaic") === true && hasStructureTrack(target) && !hasStatus(target.statuses, BIOLOGICAL_STATUS);
}
